import React, { useEffect, useState } from 'react';
import { Subject } from 'rxjs';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';

import { IconChevronUpThick, IconChevronDownThick } from '@sololearnorg/sol';

import {
  AuthService,
  Container,
  DataService,
  LearningExperienceType,
} from '../../../../../symphony';
import { useContainerData } from '../../../../../shared/public/SlHooks/useContainerData';
import { CourseProgressBar } from '../../CourseProgressBar/CourseProgressBar';
import { Certificate } from '../../Certificate/Certificate';
import { SlActions } from '../../../services/ActionsService/sl-actions';
import { SlLEContext } from '../../../global-constants';
import {
  ActionType,
  IMaterialProps,
  RequestType,
} from '../../../global-interface';

import s from './Course.scss';

export function Course({
  materialInfo,
  children,
  hideCertificate,
  hideHeader,
  className,
  isExpanded,
}: IMaterialProps): JSX.Element {
  useStyles(s);

  const [expanded, setExpanded] = useState<boolean>(isExpanded);

  useEffect(() => {
    const unsubscriber = new Subject<void>();
    const { actions$ } = Container.take(SlLEContext, SlActions);

    actions$.next({
      type: ActionType.OnCourseMount,
      requestType: RequestType.Parallel,
      data: {
        materialInfo,
        isCourseAvailable: true,
      },
    });

    return () => {
      actions$.next({
        type: ActionType.OnCourseUnmount,
        requestType: RequestType.Parallel,
      });
      unsubscriber.next();
      unsubscriber.complete();
    };
  }, []);

  const { learningExperience$: learningExperience } = useContainerData(
    'global',
    DataService,
    ['learningExperience$'],
  );

  if (!learningExperience) {
    return null;
  }

  const auth = Container.take('global', AuthService);

  const {
    header: { title },
    courseProgress,
    childrenHeight,
  } = materialInfo;

  const {
    typeId,
    name,
    description,
    uiConfigurations: { iconUrl },
  } = learningExperience;

  const courseName = typeId === LearningExperienceType.LearningPath ? title : name;

  return (
    <div className={classNames('le-course', className)}>
      {!hideHeader && (
        <div className="le-course__header">
          <div style={{ display: 'flex' }}>
            {typeId === LearningExperienceType.Course && (
              <div className="le-course__header__icon">
                <img src={iconUrl} alt={courseName} />
              </div>
            )}

            <div style={{ width: '100%', margin: 'auto' }}>
              <button
                type="button"
                onClick={() => setExpanded((val) => !val)}
                className={classNames('le-course__header__title', {
                  collapsed: !expanded,
                  path: typeId === LearningExperienceType.LearningPath,
                })}
              >
                <div>{courseName}</div>
                <div className="le-course__header__chevron">
                  {expanded ? (
                    <IconChevronUpThick size={24} />
                  ) : (
                    <IconChevronDownThick size={24} />
                  )}
                </div>
              </button>

              {typeId === LearningExperienceType.Course && (
                <div className={classNames('le-course__header__description', { collapsed: !expanded })}>
                  {description}
                </div>
              )}
            </div>
          </div>

          {auth.isLoggedIn() && (
            <div className="le-course__header__progress">
              <CourseProgressBar progress={courseProgress} />
            </div>
          )}
        </div>
      )}

      <div
        style={{
          maxHeight: expanded
            ? `${childrenHeight ? `${childrenHeight}px` : 'unset'}`
            : '0',
        }}
        className={classNames('le-course__children', { collapsed: !expanded })}
      >
        {children as React.ReactNode}
      </div>

      {!hideCertificate && (
        <div className="le-course__certificate">
          <Certificate />
        </div>
      )}
    </div>
  );
}
